'use client';

import type { ListingFormFieldError, ListingFormImage, ListingFormValues } from '@/lib/listing-form';
import { FieldError, FieldGrid, FieldLabel, TextInput, TextTextarea } from './ListingFormFields';
import ListingImageManager from './ListingImageManager';
import { listingFormStyles as s } from './listingFormStyles';

type Props = {
  values: ListingFormValues;
  errors: ListingFormFieldError[];
  disabled?: boolean;
  uploadProgress?: { current: number; total: number; fileName: string } | null;
  onFieldChange: <K extends keyof ListingFormValues>(field: K, value: ListingFormValues[K]) => void;
  onImagesChange: (images: ListingFormImage[]) => void;
};

function errFor(errors: ListingFormFieldError[], field: string): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

export default function StepPhotos({
  values,
  errors,
  disabled,
  uploadProgress,
  onFieldChange,
  onImagesChange,
}: Props) {
  return (
    <section style={s.panel}>
      <h2 style={s.sectionTitle}>Photos and description</h2>
      <p style={s.sectionHint}>
        Reorder photos, choose a cover, and add a clear description. Contact fields are optional.
      </p>

      <ListingImageManager
        images={values.images}
        onChange={onImagesChange}
        disabled={disabled}
        uploadProgress={uploadProgress}
      />
      <FieldError message={errFor(errors, 'images')} />

      <div style={{ ...s.field, marginTop: 24 }}>
        <FieldLabel htmlFor="listing-description" optional>
          Description
        </FieldLabel>
        <TextTextarea
          id="listing-description"
          name="description"
          rows={6}
          value={values.description}
          disabled={disabled}
          onChange={(e) => onFieldChange('description', e.target.value)}
          placeholder="Property condition, access, timeline, and other buyer-relevant details"
        />
      </div>

      <h3 style={{ ...s.sectionTitle, fontSize: 16, marginTop: 24 }}>Contact</h3>
      <FieldGrid cols={3}>
        <div style={s.field}>
          <FieldLabel htmlFor="listing-contact-name" optional>
            Contact name
          </FieldLabel>
          <TextInput
            id="listing-contact-name"
            name="name"
            autoComplete="name"
            value={values.contact_name}
            disabled={disabled}
            onChange={(e) => onFieldChange('contact_name', e.target.value)}
          />
        </div>
        <div style={s.field}>
          <FieldLabel htmlFor="listing-contact-phone" optional>
            Contact phone
          </FieldLabel>
          <TextInput
            id="listing-contact-phone"
            name="tel"
            autoComplete="tel"
            inputMode="tel"
            value={values.contact_phone}
            disabled={disabled}
            onChange={(e) => onFieldChange('contact_phone', e.target.value)}
          />
        </div>
        <div style={s.field}>
          <FieldLabel htmlFor="listing-contact-email" optional>
            Contact email
          </FieldLabel>
          <TextInput
            id="listing-contact-email"
            name="email"
            type="email"
            autoComplete="email"
            value={values.contact_email}
            disabled={disabled}
            error={Boolean(errFor(errors, 'contact_email'))}
            onChange={(e) => onFieldChange('contact_email', e.target.value)}
          />
          <FieldError message={errFor(errors, 'contact_email')} />
        </div>
      </FieldGrid>
    </section>
  );
}
